import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  NoData,
  RequestError,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useApprovalsTransactionsQuery } from 'hooks/queries/useApprovalsTransactionsQuery'
import { ApprovalTableTypes } from 'pages/Approvals/types'
import { getTableHeaders } from 'pages/Approvals/util'
import { ApprovalStatuses } from 'types/approvals'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

export const ExpiredTransactions = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const currentAccountReference = currentAccount?.reference || ''
  const {
    data: expiredTransactions,
    isError,
    isFetching,
  } = useApprovalsTransactionsQuery(
    currentAccountReference,
    ApprovalStatuses.EXPIRED,
    !!currentAccountReference
  )

  const {
    isSidePanelOpen: isExpiredTransactionDetailsOpen,
    sidePanelData: expiredTransactionDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(expiredTransactions)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return getTableHeaders(t, ApprovalTableTypes.EXPIRED)
  }, [t])

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!expiredTransactions) return []

    return expiredTransactions?.map((approvalTransaction, index) => {
      return {
        id: approvalTransaction?.correlationKey,
        testid: approvalTransaction?.correlationKey,
        onRowClick: () => {
          openSidePanelSummary(index)
        },
        cells: [
          {
            key: 'payment',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar size="small" name={approvalTransaction?.currency?.code} />,
            Text: `${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`,
            BottomText: approvalTransaction?.walletName,
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text:
              approvalTransaction?.beneficiaryDetails?.accountNumber ||
              approvalTransaction?.beneficiaryDetails?.destinationWallet,
            BottomText: approvalTransaction?.beneficiaryDetails?.beneficiaryName,
          },
          {
            key: 'approvalsInitiatedBy',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.initiatedBy || approvalTransaction?.initiatedByEmail,
            ...(approvalTransaction?.initiatedAt && {
              BottomText: dayjs(approvalTransaction?.initiatedAt).format(
                DateFormat.D_MMM_YYYY_AT_HH_MM
              ),
            }),
          },
          {
            key: 'dateOfExpiry',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(approvalTransaction?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM),
          },
        ],
      }
    })
  }, [expiredTransactions])

  return (
    <Box paddingB={16}>
      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isFetching && isError && <RequestError />}

      {!isFetching && !isError && !expiredTransactions?.length && (
        <NoData
          dataName={`${ApprovalStatuses.EXPIRED.toLowerCase()} transactions`}
          description={t('approvals.emptyStateDescription', {
            approvalStatus: ApprovalStatuses.EXPIRED.toLowerCase(),
          })}
        />
      )}

      {!isFetching && !isError && !!expiredTransactions?.length && (
        <>
          <DataTable name="expired-transactions-table" headers={columns} data={tableData} />
          {expiredTransactionDetails && (
            <SidePanelSummary
              isOpen={isExpiredTransactionDetailsOpen}
              paging={paging}
              onClose={() => setIsSidePanelOpen(false)}
              transactionDetails={expiredTransactionDetails}
              tab={ApprovalStatuses.EXPIRED}
            />
          )}
        </>
      )}
    </Box>
  )
}
